import { useEffect, useState } from "react";
import {
  authHeaders,
  handleUnauthorized,
} from "@/lib/auth";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const EMPTY_EMAIL = {
  name: "",
  subject: "",
  body_text: "",
  body_html: "",
  delay_days: 0,
  enabled: true,
  sequence_order: 1,
};

export default function AdminClientEmails() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState(EMPTY_EMAIL);
  const [testRecipient, setTestRecipient] = useState("");
  const [busyKey, setBusyKey] = useState("");

  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {
    try {
      setLoading(true);
      setMessage("");

      const res = await fetch(
        `${API_BASE}/api/admin/onboarding-email-templates`,
        {
          headers: {
            ...authHeaders(),
          },
        }
      );

      const data = await res.json().catch(() => []);

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        setMessage("Could not load client emails.");
        return;
      }

      setTemplates(
        Array.isArray(data)
          ? data.sort(
              (a, b) =>
                Number(a.sequence_order || 0) -
                Number(b.sequence_order || 0)
            )
          : []
      );
    } catch (err) {
      console.error(err);
      setMessage("Could not load client emails.");
    } finally {
      setLoading(false);
    }
  }

  function updateTemplate(index, field, value) {
    setTemplates((prev) =>
      prev.map((template, templateIndex) =>
        templateIndex === index
          ? {
              ...template,
              [field]: value,
            }
          : template
      )
    );
  }

  function updateNewEmail(field, value) {
    setNewEmail((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function saveTemplate(template) {
    try {
      setBusyKey(template.template_key);
      setMessage("");

      const res = await fetch(
        `${API_BASE}/api/admin/onboarding-email-templates/${template.template_key}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({
            name: template.name,
            subject: template.subject,
            body_text: template.body_text,
            body_html: template.body_html || "",
            delay_days: Number(template.delay_days || 0),
            enabled: Boolean(template.enabled),
            sequence_order: Number(
              template.sequence_order || 1
            ),
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        setMessage(
          data.detail || "Could not save client email."
        );
        return;
      }

      setMessage(`${data.name} saved.`);
      await loadTemplates();
    } catch (err) {
      console.error(err);
      setMessage("Could not save client email.");
    } finally {
      setBusyKey("");
    }
  }

  async function createTemplate() {
    if (!newEmail.name.trim()) {
      setMessage("Email name is required.");
      return;
    }

    if (!newEmail.subject.trim()) {
      setMessage("Email subject is required.");
      return;
    }

    try {
      setBusyKey("create");
      setMessage("");

      const res = await fetch(
        `${API_BASE}/api/admin/onboarding-email-templates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({
            ...newEmail,
            delay_days: Number(newEmail.delay_days || 0),
            sequence_order: Number(
              newEmail.sequence_order || 1
            ),
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        setMessage(
          data.detail || "Could not create client email."
        );
        return;
      }

      setNewEmail(EMPTY_EMAIL);
      setAdding(false);
      setMessage("Client email created.");
      await loadTemplates();
    } catch (err) {
      console.error(err);
      setMessage("Could not create client email.");
    } finally {
      setBusyKey("");
    }
  }

  async function deleteTemplate(template) {
    const confirmed = window.confirm(
      `Delete "${template.name}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setBusyKey(template.template_key);
      setMessage("");

      const res = await fetch(
        `${API_BASE}/api/admin/onboarding-email-templates/${template.template_key}`,
        {
          method: "DELETE",
          headers: {
            ...authHeaders(),
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        setMessage(
          data.detail || "Could not delete client email."
        );
        return;
      }

      setMessage("Client email deleted.");
      await loadTemplates();
    } catch (err) {
      console.error(err);
      setMessage("Could not delete client email.");
    } finally {
      setBusyKey("");
    }
  }

  async function sendTest(template) {
    if (!testRecipient.trim()) {
      setMessage(
        "Enter an email address in the Test Recipient field first."
      );
      return;
    }

    try {
      setBusyKey(`test-${template.template_key}`);
      setMessage("");

      const res = await fetch(
        `${API_BASE}/api/admin/onboarding-email-templates/${template.template_key}/test`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({
            recipient: testRecipient.trim(),
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        setMessage(
          data.detail || "Could not send test email."
        );
        return;
      }

      setMessage(`Test email sent to ${testRecipient}.`);
    } catch (err) {
      console.error(err);
      setMessage("Could not send test email.");
    } finally {
      setBusyKey("");
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <p className="text-zinc-400">
          Loading client emails...
        </p>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Client Emails
            </h1>

            <p className="text-zinc-400">
              Manage the automated onboarding email sequence
              sent to paid TapToTable clients.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setAdding((prev) => !prev)}
            className="rounded-xl bg-cyan-400 px-5 py-3 font-bold text-zinc-950"
          >
            {adding ? "Cancel" : "+ Add Email"}
          </button>
        </div>

        <section className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
          <label className="mb-2 block text-sm text-zinc-300">
            Test Recipient
          </label>

          <input
            type="email"
            value={testRecipient}
            onChange={(e) =>
              setTestRecipient(e.target.value)
            }
            placeholder="you@example.com"
            className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
          />

          <p className="mt-2 text-xs text-zinc-500">
            Used only when you click Send Test.
          </p>
        </section>

        <section className="mt-6 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-5">
          <h2 className="font-bold text-cyan-300">
            Supported Variables
          </h2>

          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            {[
              "{{restaurant_name}}",
              "{{contact_name}}",
              "{{dashboard_url}}",
              "{{login_url}}",
              "{{support_email}}",
            ].map((variable) => (
              <code
                key={variable}
                className="rounded-lg bg-zinc-950 px-3 py-2 text-zinc-300"
              >
                {variable}
              </code>
            ))}
          </div>
        </section>

        {adding && (
          <section className="mt-6 rounded-2xl border border-cyan-400/30 bg-zinc-900 p-6 space-y-5">
            <h2 className="text-xl font-bold">
              Add Client Email
            </h2>

            <EmailFields
              email={newEmail}
              onChange={updateNewEmail}
            />

            <button
              type="button"
              disabled={busyKey === "create"}
              onClick={createTemplate}
              className="rounded-xl bg-cyan-400 px-5 py-3 font-bold text-zinc-950 disabled:opacity-60"
            >
              {busyKey === "create"
                ? "Creating..."
                : "Create Email"}
            </button>
          </section>
        )}

        <div className="mt-8 space-y-6">
          {templates.map((template, index) => (
            <section
              key={template.template_key}
              className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-cyan-300">
                    Email {index + 1}
                  </div>

                  <h2 className="mt-1 text-xl font-bold">
                    {template.name}
                  </h2>

                  <p className="mt-1 text-sm text-zinc-500">
                    Key: {template.template_key}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => sendTest(template)}
                    disabled={
                      busyKey ===
                      `test-${template.template_key}`
                    }
                    className="rounded-xl border border-purple-400/40 px-4 py-2 text-sm font-semibold text-purple-300 hover:bg-purple-400/10 disabled:opacity-60"
                  >
                    Send Test
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      saveTemplate(template)
                    }
                    disabled={
                      busyKey === template.template_key
                    }
                    className="rounded-xl bg-cyan-400 px-4 py-2 text-sm font-bold text-zinc-950 disabled:opacity-60"
                  >
                    Save
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      deleteTemplate(template)
                    }
                    disabled={
                      busyKey === template.template_key
                    }
                    className="rounded-xl border border-red-500/40 px-4 py-2 text-sm font-semibold text-red-400 hover:bg-red-500/10 disabled:opacity-60"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="mt-6">
                <EmailFields
                  email={template}
                  onChange={(field, value) =>
                    updateTemplate(
                      index,
                      field,
                      value
                    )
                  }
                />
              </div>
            </section>
          ))}

          {templates.length === 0 && (
            <p className="text-zinc-500">
              No client emails have been created.
            </p>
          )}
        </div>

        {message && (
          <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900 p-4 text-sm text-zinc-300">
            {message}
          </div>
        )}

        <p className="mt-6 text-xs text-zinc-500">
          Changes affect future onboarding sends. Emails
          already sent are not changed.
        </p>
      </div>
    </main>
  );
}

function EmailFields({
  email,
  onChange,
}) {
  return (
    <div className="grid gap-5">
      <Field
        label="Email Name"
        value={email.name}
        onChange={(value) =>
          onChange("name", value)
        }
      />

      <Field
        label="Subject"
        value={email.subject}
        onChange={(value) =>
          onChange("subject", value)
        }
      />

      <Field
        label="Email Body"
        value={email.body_text}
        onChange={(value) =>
          onChange("body_text", value)
        }
        textarea
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <NumberField
          label="Send After Days"
          value={email.delay_days}
          min={0}
          max={365}
          onChange={(value) =>
            onChange("delay_days", value)
          }
        />

        <NumberField
          label="Sequence Order"
          value={email.sequence_order}
          min={1}
          max={100}
          onChange={(value) =>
            onChange("sequence_order", value)
          }
        />
      </div>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={Boolean(email.enabled)}
          onChange={(e) =>
            onChange(
              "enabled",
              e.target.checked
            )
          }
          className="h-4 w-4"
        />

        <span className="text-sm text-zinc-300">
          Enabled
        </span>
      </label>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  textarea = false,
}) {
  return (
    <div>
      <label className="mb-2 block text-sm text-zinc-300">
        {label}
      </label>

      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) =>
            onChange(e.target.value)
          }
          rows={10}
          className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) =>
            onChange(e.target.value)
          }
          className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
        />
      )}
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}) {
  return (
    <div>
      <label className="mb-2 block text-sm text-zinc-300">
        {label}
      </label>

      <input
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value)
        }
        className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
      />
    </div>
  );
}
